import React from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LoopIcon from "@mui/icons-material/Loop";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
  LIVE_EXERCISE_MODES,
  liveExerciseModeCopyKeys,
} from "../../utils/liveExerciseMode";
import { INTERVAL_PHASES, formatIntervalClock } from "../../utils/intervalTimer";
import "./LiveTrainingModeTools.css";

const MODE_ICONS = {
  [LIVE_EXERCISE_MODES.KOT]: EmojiEventsIcon,
  [LIVE_EXERCISE_MODES.SEQUENCES]: LoopIcon,
  [LIVE_EXERCISE_MODES.MULTIBALL]: SportsTennisIcon,
  [LIVE_EXERCISE_MODES.MATCH]: ScoreboardIcon,
  [LIVE_EXERCISE_MODES.INTERVAL]: HourglassBottomIcon,
  [LIVE_EXERCISE_MODES.OFF_TABLE]: DirectionsRunIcon,
  [LIVE_EXERCISE_MODES.TABLE_DRILL]: TableRestaurantIcon,
};

const MODE_TITLE_FALLBACK = {
  [LIVE_EXERCISE_MODES.KOT]: "King of the Table",
  [LIVE_EXERCISE_MODES.SEQUENCES]: "Sequences",
  [LIVE_EXERCISE_MODES.MULTIBALL]: "Multiball",
  [LIVE_EXERCISE_MODES.MATCH]: "Match play",
  [LIVE_EXERCISE_MODES.INTERVAL]: "Interval timer",
  [LIVE_EXERCISE_MODES.OFF_TABLE]: "Off-table",
  [LIVE_EXERCISE_MODES.TABLE_DRILL]: "Table drill",
};

/**
 * Coach dashboard strip: how this block runs and the tools that fit it.
 */
export function LiveTrainingModeStrip({
  profile,
  t,
  interval = null,
  intervalRemainingSec = 0,
  matchSummary = null,
  showCountAction = false,
  countLabel = "",
  onOpenInterval = null,
  onOpenMatch = null,
  onOpenCount = null,
}) {
  if (!profile) return null;
  const Icon = MODE_ICONS[profile.mode] || TableRestaurantIcon;
  const { titleKey, briefKey } = liveExerciseModeCopyKeys(profile);
  const title = t(titleKey, MODE_TITLE_FALLBACK[profile.mode] || "");
  const brief = t(briefKey, "");
  const intervalLive =
    interval &&
    (interval.phase === INTERVAL_PHASES.WORK || interval.phase === INTERVAL_PHASES.REST);

  return (
    <section
      className={`lt-mode-strip lt-mode-strip--${profile.mode}`}
      data-tour-id="live-coach-mode-strip"
      aria-label={title}
    >
      <span className="lt-mode-strip__icon" aria-hidden>
        <Icon fontSize="small" />
      </span>
      <div className="lt-mode-strip__text">
        <p className="lt-mode-strip__title">{title}</p>
        {brief ? <p className="lt-mode-strip__brief">{brief}</p> : null}
      </div>

      <div className="lt-mode-strip__actions">
        {profile.tools.interval && onOpenInterval ? (
          <button
            type="button"
            className={`lt-mode-strip__btn${
              intervalLive ? ` lt-mode-strip__btn--live lt-mode-strip__btn--${interval.phase}` : ""
            }`}
            onClick={onOpenInterval}
          >
            <HourglassBottomIcon fontSize="small" />
            {intervalLive ? (
              <span className="lt-mode-strip__readout">
                {interval.phase === INTERVAL_PHASES.WORK
                  ? t("liveModeIntervalWork", "Work")
                  : t("liveModeIntervalRest", "Rest")}{" "}
                {interval.round}/{interval.rounds} · {formatIntervalClock(intervalRemainingSec)}
                {interval.running ? null : ` · ${t("liveModeIntervalPaused", "Paused")}`}
              </span>
            ) : (
              t("liveModeIntervalOpen", "Interval timer")
            )}
          </button>
        ) : null}
        {profile.tools.match && onOpenMatch ? (
          <button
            type="button"
            className="lt-mode-strip__btn lt-mode-strip__btn--primary"
            onClick={onOpenMatch}
          >
            <ScoreboardIcon fontSize="small" />
            {t("liveModeMatchOpen", "Scoreboard")}
            {matchSummary ? (
              <span className="lt-mode-strip__badge">{matchSummary}</span>
            ) : null}
          </button>
        ) : null}
        {showCountAction && onOpenCount ? (
          <button type="button" className="lt-mode-strip__btn" onClick={onOpenCount}>
            <FormatListNumberedIcon fontSize="small" />
            {countLabel || t("liveTrainingCountModeBtn", "Count")}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default LiveTrainingModeStrip;
